/**
 * useWebhookProviders — Preferences > Webhook Providers screen VM.
 * Kiran 2026-06-18.
 *
 * Operator flow:
 *   - list providers (CONFIG_KV-backed)
 *   - add a new provider (slug + display_name)
 *   - update display_name + event mappings
 *   - rotate HMAC signing secret
 *   - delete a provider (+ its secret + its mappings)
 *
 * Raw secret values are NEVER returned by reads — the API ships
 * has_secret + a short fingerprint so the UI can show "the right
 * secret is set" without exposing it.
 */

import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { APIROUTES, fillPath } from '../constants/routes.constants'
import { mutateJson } from '../utils/api/mutateJson'
import { parseApiError } from '../utils/api/parseApiError'
import type { OrderStatus } from '../constants/order-status.constants'

export interface WebhookProviderDto {
  readonly provider: string
  readonly display_name: string
  readonly events: Record<string, OrderStatus>
  readonly has_secret: boolean
  readonly secret_fingerprint: string | null
}

interface ListBody {
  readonly success: true
  readonly data: { readonly items: WebhookProviderDto[] }
}

export interface UseWebhookProvidersReturn {
  readonly items: ReadonlyArray<WebhookProviderDto>
  readonly isLoading: boolean
  readonly error: string | null
  readonly reload: () => void
  readonly create: (input: {
    provider: string
    display_name: string
    events: Record<string, OrderStatus>
  }) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
  readonly update: (
    provider: string,
    patch: { display_name?: string; events?: Record<string, OrderStatus> }
  ) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
  readonly remove: (provider: string) => Promise<{ ok: true } | { ok: false; message: string }>
  readonly rotateSecret: (
    provider: string,
    value: string
  ) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
}

export function useWebhookProviders(): UseWebhookProvidersReturn {
  const { authedFetch } = useAuth()
  const [items, setItems] = useState<WebhookProviderDto[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let cancelled = false
    const ctrl = new AbortController()
    setIsLoading(true)
    setError(null)
    void (async () => {
      try {
        const res = await authedFetch(APIROUTES.PREFERENCESWEBHOOKPROVIDERS, { signal: ctrl.signal })
        if (!res.ok) throw new Error((await parseApiError(res)).message)
        const body = (await res.json()) as ListBody
        if (!cancelled) setItems(body.data.items)
      } catch (err) {
        if (cancelled) return
        if (err instanceof DOMException && err.name === 'AbortError') return
        setError(err instanceof Error ? err.message : 'Failed to load')
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    })()
    return () => {
      cancelled = true
      ctrl.abort()
    }
  }, [authedFetch, reloadKey])

  const reload = useCallback(() => setReloadKey((k) => k + 1), [])

  const create = useCallback(
    async (input: { provider: string; display_name: string; events: Record<string, OrderStatus> }) => {
      const r = await mutateJson<WebhookProviderDto>(
        authedFetch,
        APIROUTES.PREFERENCESWEBHOOKPROVIDERS,
        'POST',
        input
      )
      if (r.ok) {
        reload()
        return { ok: true as const }
      }
      return { ok: false as const, field: r.field, message: r.message }
    },
    [authedFetch, reload]
  )

  const update = useCallback(
    async (
      provider: string,
      patch: { display_name?: string; events?: Record<string, OrderStatus> }
    ) => {
      const r = await mutateJson<WebhookProviderDto>(
        authedFetch,
        fillPath(APIROUTES.PREFERENCESWEBHOOKPROVIDERITEM, { provider }),
        'PATCH',
        patch
      )
      if (r.ok) {
        reload()
        return { ok: true as const }
      }
      return { ok: false as const, field: r.field, message: r.message }
    },
    [authedFetch, reload]
  )

  const remove = useCallback(
    async (provider: string) => {
      const r = await mutateJson<{ ok: true }>(
        authedFetch,
        fillPath(APIROUTES.PREFERENCESWEBHOOKPROVIDERITEM, { provider }),
        'DELETE'
      )
      if (r.ok) {
        reload()
        return { ok: true as const }
      }
      return { ok: false as const, message: r.message }
    },
    [authedFetch, reload]
  )

  const rotateSecret = useCallback(
    async (provider: string, value: string) => {
      const r = await mutateJson<{ secret_fingerprint: string }>(
        authedFetch,
        fillPath(APIROUTES.PREFERENCESWEBHOOKPROVIDERSECRET, { provider }),
        'POST',
        { value }
      )
      if (r.ok) {
        reload()
        return { ok: true as const }
      }
      return { ok: false as const, field: r.field, message: r.message }
    },
    [authedFetch, reload]
  )

  return { items, isLoading, error, reload, create, update, remove, rotateSecret }
}
